import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SessionService } from '@monsido/core/session/session.service';
import { PolicyLineChartService } from '@client/app/blocks/service/charts/policy-line-chart/policy-line-chart.service';
import { ComplianceService } from '@client/app/blocks/service/compliance/compliance.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';

@Component({
    selector: 'policy-last-scan',
    standalone: false,
    template: `
        <mon-chart-dashboard
            [progress]="promise"
            [doughnutLabels]="doughnutLabels"
            [doughnutData]="doughnutData"
            [doughnutColors]="doughnutColors"
            [doughnutOptions]="doughnutOptions"
            [lineLabels]="lineChartLabels"
            [lineData]="lineChartData"
            [lineSeries]="lineChartSeries"
            [lineColors]="lineColors"
            [lineOptions]="lineOptions"
            [lineOverride]="lineOverride"
            [cards]="cardsList">
        </mon-chart-dashboard>
    `,
})
export class PolicyLastScanComponent implements OnInit, OnChanges {
    @Input() history: any;
    @Input() pagesCount: any;
    @Input() pagesErrorsCount: any;
    @Input() linksCount: any;
    @Input() policies: any;

    domain: any;
    latestCrawl: any;
    cardsList: any[] = [];
    doughnutLabels: any;
    doughnutData: any;
    doughnutColors: any;
    doughnutOptions: any;
    lineChartLabels: any;
    lineChartData: any;
    lineChartSeries: any;
    lineColors: any;
    lineOptions: any;
    lineOverride: any;
    promise: any;

    constructor (
        private pipeService: PipeService,
        private translateService: TranslateService,
        private sessionService: SessionService,
        private policyLineChartService: PolicyLineChartService,
        private complianceService: ComplianceService,
    ) {}

    ngOnInit () {
        this.activate();
    }

    activate () {
        this.domain = this.sessionService.domain;
        this.latestCrawl = this.getLatestHistory();
        this.cardsList = [];

        this.setupLineChart();
        this.setupDoughnutChart();
        this.setupNotifications();
    }

    ngOnChanges (changes: SimpleChanges) {
        this.setupLineChart();
        this.setupDoughnutChart();
        this.setupNotifications();
    }

    getLatestHistory () {
        if (this.history.length > 0) {
            return this.history.sort((a: any, b: any) => {
                // Get the latest history id
                return a.id > b.id ? -1 : a.id < b.id ? 1 : 0;
            })[0];
        } else {
            return {};
        }
    }

    setupLineChart () {
        const lineChartService = this.policyLineChartService.createPolicyLineChartService();
        this.lineChartData = lineChartService.getData(this.history);
        this.lineChartLabels = lineChartService.getLabels(this.history);
        this.lineOverride = lineChartService.getOverride();
        this.lineChartSeries = lineChartService.getSeries();
        this.lineColors = lineChartService.getColors();
        this.lineOptions = lineChartService.getOption();
    }

    setupDoughnutChart () {
        this.doughnutLabels = ['', ''];
        this.doughnutColors = ['#4dc0bd', '#DCDCDC'];

        this.doughnutData = this.complianceService.policyCompliance(
            this.policies,
            this.pagesCount,
            this.linksCount,
        );

        this.doughnutOptions = {
            centerText: {
                percentageLabel: this.pipeService.get('monNumber')(
                    this.complianceService.policyCompliancePercentage(
                        this.policies,
                        this.pagesCount,
                        this.linksCount,
                    ) / 100,
                    '0.[00] %',
                ),
                totalLabel: this.translateService.getString('Policy'),
                subLabel: this.translateService.getString('Compliance'),
                subFontSize: 170,
                totalFontSize: 170,
            },
            cutoutPercentage: 80,
            tooltips: {
                enabled: true,
                callbacks: {
                    label: (tooltipItem: any, data: any) => {
                        if (tooltipItem.index === 0) {
                            return this.translateService.getString('Succeeds');
                        }
                        return this.translateService.getString('Errors');
                    },
                },
            },
            hover: {
                mode: null,
            },
        };
    }

    setupNotifications () {
        const policiesInError = this.policies.filter((policy: any) => {
            return policy.matches_count > 0;
        });
        this.cardsList = [
            {
                title: this.translateService.getString('Policies with violations'),
                issues: policiesInError.length,
            },
            {
                title: this.translateService.getString('Content with policy violations'),
                tooltipTitle: this.translateService.getString('Includes both documents and pages'),
                issues: this.pagesErrorsCount,
            },
        ];
    }
}
